// Optimistic mirror of the server-side reorder algorithms. The backend twins are
// SectionController.UpdateSection, ChapterController.UpdateChapter,
// ProjectSectionController.UpdateSection and ProjectChapterController.UpdateChapter —
// change one, change this (and see feedback on drag-and-drop: the UI must reproduce what
// the backend will actually store, not an idealised 1..N renumbering).
//
// What the server does, in all four: it does NOT repack the list. It shifts only the rows
// BETWEEN the dragged row's old position and its new one by ±1, then stamps the dragged row
// with the values it was sent. So a naive `map((x, i) => i + 1)` is only correct while the
// column happens to be a gapless 1..N — and Number is NOT: every delete path repacks
// SortOrder and deliberately leaves Number alone, so one delete puts a permanent gap in it.
//
// `renumber` distinguishes the two families:
//   true  — admin/master catalog: Number is a repositionable label and moves with SortOrder.
//   false — inside a project: Number is a frozen snapshot of the UD label the section/chapter
//           had when it was added, and the server never touches it. The UI must not either.
//
// Only enabled rows are passed in, which matches the chapter controllers' `Enabled` filter.
// The section controllers additionally shift disabled rows (a known backend inconsistency,
// tracked for the OrderingService extraction) — that cannot change any enabled row's result,
// so the visible mirror stays exact either way.
export function applyReorder(items, activeId, overId, { renumber }) {
    const dragged = items.find(i => i.Id === activeId);
    const target = items.find(i => i.Id === overId);
    if (!dragged || !target || dragged.Id === target.Id)
        return null;

    const targetSortOrder = target.SortOrder;
    const targetNumber = target.Number;
    const movingUp = dragged.SortOrder < targetSortOrder;

    const shifted = items.map(item => {
        if (item.Id === dragged.Id) {
            return renumber
                ? { ...item, SortOrder: targetSortOrder, Number: targetNumber }
                : { ...item, SortOrder: targetSortOrder };
        }

        const shiftsDown = movingUp && item.SortOrder <= targetSortOrder && item.SortOrder > dragged.SortOrder;
        const shiftsUp = !movingUp && item.SortOrder >= targetSortOrder && item.SortOrder < dragged.SortOrder;
        if (!shiftsDown && !shiftsUp)
            return item;

        const delta = shiftsDown ? -1 : 1;
        return renumber
            ? { ...item, SortOrder: item.SortOrder + delta, Number: item.Number + delta }
            : { ...item, SortOrder: item.SortOrder + delta };
    });

    return {
        reordered: shifted.sort((a, b) => a.SortOrder - b.SortOrder),
        targetSortOrder,
        targetNumber
    };
}
